import React, { useEffect, useRef, useState } from "react";
import { ContentModal } from "../../components/content-modal/ContentModal";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { BaseLogo, ImageLogo } from "../../../core/models/entities/BaseLogo";
import { usePageQuery } from "../../shared/hooks/usePageQuery";
import { getInitialQuery } from "../../shared/functions/getInitialQuery.function";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { LogoListWrapper } from "./subcomponents/logo-list.wrapper";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { LogoDetailsWrapper } from "./subcomponents/logo-details.wrapper";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";
import { RejectionNoteDialog } from "../../components/Dialogs/rejection-note-popup.component";
import { useDialog } from "../../components/Dialogs/dialog-base";
import { LogoTitleHeader } from "../../components/Logo/LogoTitleHeader";
import { useOpenLogo } from "./vmHooks/useOpenLogo";
import { useLogoActionHandler } from "./hooks/useLogoActionsHandler";
import { consumeLogoListStaleFlag } from "../../../utils/functions/logo-refresh.util";
import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { getAllQuery } from "../logo-editor/logo-editor.screen";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import { useStorageFreshness } from "../../../hooks/useStorageFreshness";
import { resolveStorageFreshnessOrgId } from "../../../utils/functions/storageFreshness";
import { getActAsOrgId } from "../../../contexts/actAsStore";
import { useLogoListPendingPoll } from "../../../hooks/useLogoListPendingPoll";
import { cachedDesignListTotal } from "../../../data/repositories/DesignRepository";
import { shouldCrossPrefetchDesigns } from "../../../utils/functions/designPrefetch";

export function LogoListScreen(): React.ReactElement {
  const [query, queryChanger] = usePageQuery();
  const { state, dataTransition } = usePagedComponentStateMachine();
  const {
    logoRepository,
    organizationRepository,
    designRepository,
    styleRepository,
    manufacturerRepository,
    designFolderRepository,
  } = useRepositoriesContext();
  const [lastPage, setLastPage] = useState<number>(1);
  const [logoSelected, setLogoSelected] = useState<BaseLogo | null>(null);
  const [logoRejection, setLogoRejection] = useState<BaseLogo | null>(null);
  const { profile, role, organizationId } = useProfileContext();
  const rejectionNoteDialog = useDialog();
  // useSuspense:false - the default suspends the WHOLE screen until
  // translation.json is fetched, replacing the loading view below with the
  // bare route fallback exactly when loading is slowest (cold landing). Not
  // ready yet → t falls back to the defaultValue passed at the call site.
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { result: openResult, clearOpen } = useOpenLogo();
  const { handleGoToEditLogo } = useLogoActionHandler();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();
  const [updatingLogoId, setUpdatingLogoId] = useState(
    location.state?.updatingLogoId
  );

  useEffect(() => {
    if (location.state?.updatingLogoId) {
      setUpdatingLogoId(location.state.updatingLogoId);
    }
  }, [location]);

  function showRejectionNote(logo: BaseLogo) {
    setLogoRejection(logo);
    rejectionNoteDialog.openDialog();
  }

  function handleCancelRejectionNote() {
    rejectionNoteDialog.closeDialog();
  }

  const {
    data: page,
    isSuccess: isLogosSuccess,
    isLoading: isLogosLoading,
    isError: isLogosError,
    isFetching: isLogosFetching,
    isPreviousData: isLogosPreviousData,
    refetch: refetchLogos,
  } = logoRepository.useAllLogos(query, { keepPreviousData: true });

  // Stock freshness on view (flag): the storage rows behind this page's
  // counts that are stale (> 24 h or never verified) are refreshed with ONE
  // background call and patched into the cache - only once the page has
  // settled (keepPreviousData: while previous data is shown the rows belong
  // to the old query). The detail popup below shares the same settled state
  // and the same call. Same org resolution as GetAllLogos server-side; an
  // admin without act-as (org 0) idles. Flag off = no-op.
  const storageFreshnessOrgId = resolveStorageFreshnessOrgId(
    getActAsOrgId(),
    role,
    organizationId
  );
  const isLogosSettled =
    isLogosSuccess && !isLogosFetching && !isLogosPreviousData;
  useStorageFreshness(storageFreshnessOrgId, page?.results, isLogosSettled);

  // "Prices on their way" / "Preparing logo…" rows (flags productPending /
  // cloneInstant): the return-to-list refetch above (consumeLogoListStaleFlag)
  // races the API worker, and nothing else ever writes a landed GetLogo back
  // into a list row - so the badge stayed until F5. Bounded re-read of the
  // pending rows themselves (3/8/15 s, max 3 ticks, only rows younger than
  // 10 min, only once the page has settled): <= 3 GetLogo per pending row -
  // written to the row's real detail entry so a popup opened next costs
  // nothing - or, above 3 pending rows, ONE list refetch per tick. The row
  // open in the popup below (LogoDetailsWrapper, rendered inside this
  // screen) polls the same detail key itself, so the list leaves it alone:
  // one poller per key, never 6 GetLogo on one logo. Build-time constant
  // hook (no body with both flags off).
  useLogoListPendingPoll({
    rows: page?.results,
    settled: isLogosSettled,
    refetchList: refetchLogos,
    excludeIds: logoSelected ? [logoSelected.id] : undefined,
  });

  const {
    data: options,
    isSuccess: isOptionsSuccess,
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = logoRepository.useOptions();

  const {
    customers,
    vendors,
    isError: isOrganizationsError,
    isSuccess: isOrganizationsSuccess,
    isLoading: isOrganizationsLoading,
  } = useCustomerAndVendors(organizationRepository);

  if (page && page.pageCount !== lastPage) {
    setLastPage(page.pageCount);
  }

  // keepPreviousData: while a new search/page is loading, the paginator still
  // shows the previous result's page range, so the user can click a page
  // beyond the new result's last page. Once fresh data lands, clamp back to
  // the (new) last page. No-op when the index is already in range.
  useEffect(() => {
    if (!page || isLogosPreviousData) return;
    // An empty result (pageCount 0) would otherwise leave the index stranded
    // (e.g. "page 7 of 0") - park it on page 1.
    const lastValidPage = Math.max(page.pageCount, 1);
    if (query.page.index > lastValidPage) {
      queryChanger.handleNewPage({ value: String(lastValidPage) });
    }
  }, [page, isLogosPreviousData, query.page.index, queryChanger]);

  // A tab/filter switch already fires a fresh heavy list call; the automatic
  // next-page prefetch on top of it DOUBLED the backend load per switch (and
  // on the landing page). Arm the auto-prefetch only once the user has
  // actually paged within the current filter combination — hover-prefetch
  // (handlePrefetchPage) still covers real paging intent instantly.
  const filterSignature = JSON.stringify(query.filterBy ?? []);
  const prefetchArmedRef = useRef(false);
  const prevFilterSigRef = useRef(filterSignature);
  useEffect(() => {
    if (prevFilterSigRef.current !== filterSignature) {
      prevFilterSigRef.current = filterSignature;
      prefetchArmedRef.current = false;
    }
    if (query.page.index > 1) {
      prefetchArmedRef.current = true;
    }
  }, [filterSignature, query.page.index]);

  // Warm the next page's cache so sequential paging (1->2->3...) is instant.
  // Prefetch only -- it does not alter the current query, so price freshness
  // and the page state-machine are unchanged.
  useEffect(() => {
    if (!prefetchArmedRef.current) return;
    // Skip on page 1 of a search: every keystroke would otherwise trigger a
    // hidden second heavy backend call for a next page the user rarely
    // visits. Once the user PAGES within search results (index > 1) they have
    // settled on the term, and instant next-page is worth it again. Also skip
    // while previous data is shown (keepPreviousData): its pageIndex/pageCount
    // belong to the old query, so wait for fresh data (this effect re-runs
    // when isLogosPreviousData flips back to false).
    if (!page || page.pageIndex >= page.pageCount) return;
    if ((query.searchTerm && query.page.index === 1) || isLogosPreviousData)
      return;
    // Debounce: when the user pages rapidly and non-sequentially (1 -> 25 -> 49),
    // only prefetch the page they settle on, not every page they skip past -
    // otherwise each jump piles on a heavy background list fetch.
    const timer = setTimeout(() => {
      logoRepository.prefetchLogos({
        ...query,
        page: { ...query.page, index: query.page.index + 1 },
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    page?.pageIndex,
    page?.pageCount,
    query.page.index,
    query.searchTerm,
    isLogosPreviousData,
  ]);

  // Users hop Logo <-> Design constantly: warm the design list's gates so the
  // first hop over is instant. Two waves with different timing:
  //
  // LIGHT wave (styles + manufacturers, small static master data): fires at
  // MOUNT with the eager-prefetch flag ON, in parallel with the heavy logos
  // call; flag OFF = once this list has loaded (the old behavior).
  //
  // The design FIRST PAGE is itself a heavy list query and stays gated on
  // `page` in BOTH modes: fired at mount it would compete with the 17-20s
  // logos call for the browser's per-origin connections (making the response
  // the user is waiting for land LATER), and it would fire on error landings
  // where nothing gets used.
  // getInitialQuery(new URLSearchParams()) is exactly what design-list
  // computes on a fresh visit, so the cache key matches by construction.
  const lightPrefetchTrigger = enviroment.eagerPrefetchEnabled || !!page;
  useEffect(() => {
    if (lightPrefetchTrigger) {
      styleRepository.prefetchStyles(getAllQuery);
      manufacturerRepository.prefetchManufacturers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightPrefetchTrigger]);
  const logosLoaded = !!page;
  useEffect(() => {
    // Flag designPrefetchLeanEnabled: skipped ONLY when the cache already
    // holds the design first page with a total over designPrefetchMaxTotal
    // (a memory read of the key prefetchDesigns writes - never a call to
    // find out); not cached / no total = today. Flag off = the cache is not
    // even read and the prefetch fires exactly as today.
    if (
      logosLoaded &&
      shouldCrossPrefetchDesigns(
        enviroment.designPrefetchLeanEnabled
          ? cachedDesignListTotal(
              queryClient,
              getInitialQuery(new URLSearchParams())
            )
          : undefined,
        enviroment.designPrefetchMaxTotal,
        enviroment.designPrefetchLeanEnabled
      )
    ) {
      designRepository.prefetchDesigns(getInitialQuery(new URLSearchParams()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logosLoaded]);

  // Folders (eager-prefetch flag only): useFolders lives in LogoListWrapper's
  // view model, which only mounts once logos+options+organizations have ALL
  // succeeded - so the small folders call queued behind the heavy logos call
  // too. prefetchFolders owns the role/orgId guard (single source of truth;
  // it no-ops for customers and while the profile - and thereby orgId - is
  // still loading; the effect re-runs when they arrive).
  // Fail-silent: on any error useFolders simply fetches as before.
  useEffect(() => {
    if (!enviroment.eagerPrefetchEnabled) return;
    designFolderRepository.prefetchFolders(organizationId, role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, organizationId]);

  // Hover-prefetch for the paginator: warm the hovered page with exactly the
  // request a click would issue (prefetchLogos shares the click's cache
  // key and staleTime, so this never changes what a click shows).
  function handlePrefetchPage(pageIndex: number) {
    // While previous data is shown (keepPreviousData) the paginator's page
    // range belongs to the OLD query - hovering it must not fire heavy calls
    // for potentially out-of-range pages of the new one.
    if (isLogosPreviousData) return Promise.resolve();
    return logoRepository.prefetchLogos({
      ...query,
      page: { ...query.page, index: pageIndex },
    });
  }

  useEffect(() => {
    if (openResult) {
      setLogoSelected(openResult);
    }
  }, [openResult]);

  useEffect(() => {
    if (consumeLogoListStaleFlag()) {
      setIsRefreshing(true);
      queryClient.invalidateQueries({ queryKey: ["logos"] }).then(() => {
        setIsRefreshing(false);
      });
    }
  }, [queryClient]);

  const isLoading =
    isOptionsLoading ||
    isLogosLoading ||
    isOrganizationsLoading ||
    isRefreshing;
  const isSuccess =
    isLogosSuccess && isOptionsSuccess && isOrganizationsSuccess;
  const isError = isOptionsError || isLogosError || isOrganizationsError;
  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <>
      <LogoListWrapper
        query={query}
        queryChanger={queryChanger}
        isPageLoading={state === PagedComponentState.PageLoading}
        isFetching={isLogosFetching}
        isPreviousData={isLogosPreviousData}
        totalCount={page?.totalCount}
        lastPage={lastPage}
        onPrefetchPage={handlePrefetchPage}
        onSelectLogo={setLogoSelected}
        logos={page?.results}
        profile={profile!}
        customers={customers!}
        vendors={vendors}
        options={options!}
        onViewRejectNote={showRejectionNote}
        updatingLogoId={updatingLogoId}
      />
      {logoRejection && (
        <RejectionNoteDialog
          title={<LogoTitleHeader logo={logoRejection!} />}
          dialogOptions={rejectionNoteDialog}
          approval={(logoRejection as ImageLogo).approval!}
          exclude={role!}
          onEdit={() => handleGoToEditLogo(logoRejection)}
          onCancel={handleCancelRejectionNote}
          logo={logoRejection}
        />
      )}
      <ContentModal
        open={!!logoSelected}
        onClose={() => {
          setLogoSelected(null);
          clearOpen();
        }}
      >
        <LogoDetailsWrapper
          logo={logoSelected!}
          onViewRejectNote={showRejectionNote}
          closeDialog={() => setLogoSelected(null)}
          listSettled={isLogosSettled}
        />
      </ContentModal>
    </>
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    // The logos call can take 15-20s on a cold landing: show visible activity
    // (indeterminate progressbar + text) instead of a static gray surface
    // that reads as an empty/failed page.
    <LoadingView
      text={t("logoList.loadingLogos", "Loading logos…")}
      showProgressBar
    />
  );
}
